import React from 'react';

import { ApolloProvider } from '@apollo/client';
import { CssBaseline } from '@mui/material';
import {
  ThemeProvider,
  createTheme
} from '@mui/material/styles';
import { BeforeAndAfterActivity } from 'activities/BeforeAndAfterActivity/BeforeAndAfterActivity';
import { DebugActivity } from 'activities/DebugActivity/DebugActivity';
import { FundraiseActivity } from 'activities/FundraiseActivity/FundraiseActivity';
import { HomeActivity } from 'activities/HomeActivity/HomeActivity';
import { InvestorProfileActivity } from 'activities/InvestorProfileActivity/InvestorProfileActivity';
import { InvestorsActivity } from 'activities/InvestorsActivity/InvestorsActivity';
import { LegalActivity } from 'activities/LegalActivity/LegalActivity';
import { LegalDocumentActivity } from 'activities/LegalDocumentActivity/LegalDocumentActivity';
import { MyFundraisesActivity } from 'activities/MyFundraisesActivity/MyFundraisesActivity';
import { MyProfileActivity } from 'activities/MyProfileActivity/MyProfileActivity';
import { PitchDecksActivity } from 'activities/PitchDecksActivity/PitchDecksActivity';
import { PitchDeckTemplateActivity } from 'activities/PitchDeckTemplateActivity/PitchDeckTemplateActivity';
import {
  apolloService,
  ModalProvider,
  RestrictToSubscriptionProvider,
  routeDirectory,
  SnackbarProvider
} from 'modules';
import {
  BrowserRouter,
  Route,
  Routes
} from 'react-router-dom';

import * as auth from './activities/auth';
import * as superAdmin from './activities/superAdmin';
import * as MarkerWidget from './MarkerWidget';

import './App.css';



const theme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1360
    }
  },

  palette: {
    primary: {
      main: '#E16B50'
    },

    secondary: {
      main: '#1E3949'
    },

    text: {
      primary: '#1E3949'
    }
  },

  components: {
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          '& .MuiAutocomplete-popupIndicator': {
            color: '#E16B50'
          }
        }
      }
    },

    MuiAccordion: {
      styleOverrides: {
        // This gets rid of the pesky grey line above the accordion
        root: {
          '&:before': {
            display: 'none'
          }
        }
      }
    },

    MuiContainer: {
      defaultProps: {
        maxWidth: 'xl'
      }
    },

    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: '#486C82'
        }
      }
    },

    MuiDialogActions: {
      styleOverrides: {
        root: {
          marginRight: 8,
          marginBottom: 8
        }
      }
    },

    MuiInputBase: {
      styleOverrides: {
        root: {
          backgroundColor: '#FFFFFF'
        }
      }
    },
    MuiTextField: {
      defaultProps: {
        size: 'small'
      }
    },

    MuiSelect: {
      defaultProps: {
        size: 'small'
      }
    },

    MuiFormControl: {
      defaultProps: {
        size: 'small'
      }
    },

    MuiButton: {
      styleOverrides: {
        outlinedSecondary: {
          borderColor: '#dbdfe1'
        }
      },

      defaultProps: {
        variant: 'contained'
      }
    },

    MuiLink: {
      styleOverrides: {
        root: {
          color: '#528898'
        }
      },
      defaultProps: {
        underline: 'none'
      }
    },


    MuiTabs: {
      styleOverrides: {
        root: {
          height: 40
        },
        indicator: {
          backgroundColor: '#486C82',
          height: '100%',
          zIndex: 0,
          borderRadius: 24,
          color: 'white'
        }
      }
    },

    MuiTab: {
      styleOverrides: {
        root: {
          zIndex: 1
        },
        textColorPrimary: {
          color: '#92a7b4',
          fontWeight: 'bold',
          letterSpacing: 0.15
        }
      }
    },

    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontFamily: 'Noto Serif',
          fontWeight: 'bold'
        }
      }
    },

    MuiListItemIcon: {
      defaultProps: {
        style: {
          color: '#87ACB9'
        }
      }
    },

    MuiListItemText: {
      defaultProps: {
        disableTypography: true,
        style: {
          color: '#528898'
        }
      }
    },

    MuiTypography: {
      styleOverrides: {
        root: {
          color: '#1E3949',
          fontFamily: ['Roboto', 'Noto Serif'].join(',')
        },
        body1: {
          color: '#486C82'
        },
        body2: {
          color: '#486C82'
        },
        h1: {
          fontFamily: 'Noto Serif',
          fontSize: 34,
          fontWeight: 500,
          letterSpacing: 0.25
        },
        h2: {
          fontFamily: 'Noto Serif',
          fontSize: 24,
          fontWeight: 500
        },
        h3: {
          fontFamily: 'Noto Serif',
          fontSize: 18,
          fontWeight: 500,
          margin: 0
        },
        h4: {
          marginBottom: 16,
          fontSize: 14,
          fontWeight: 500
        },
        h5: {
          fontSize: 14,
          fontWeight: 400
        }
      }
    }
  }
});




function App() {
  const [client, setClient] = React.useState<apolloService.IApolloClient | null>(null);



  React.useEffect(() => {
    apolloService.refreshClient();
    setClient(apolloService.getClient());
  }, []);



  return client ? (
    <ApolloProvider client={client}>
      <ThemeProvider theme={theme}>
        <CssBaseline />

        <BrowserRouter>
          <Routes>
            <Route
              path={routeDirectory.home.rawPath}
              element={<HomeActivity />} />

            {auth.routes}
            {superAdmin.routes}

            <Route
              path={routeDirectory.myProfile.rawPath}
              element={<MyProfileActivity />} />

            <Route
              path={routeDirectory.investorProfile.rawPath}
              element={<InvestorProfileActivity />} />

            <Route
              path={routeDirectory.pitchDecks.rawPath}
              element={<PitchDecksActivity />} />

            <Route
              path={routeDirectory.pitchDecks.group.rawPath}
              element={<PitchDeckTemplateActivity />} />

            <Route
              path={routeDirectory.pitchDecks.title.rawPath}
              element={<PitchDeckTemplateActivity />} />

            <Route
              path={routeDirectory.investors.rawPath}
              element={<InvestorsActivity />} />

            <Route
              path={routeDirectory.myFundraises.rawPath}
              element={<MyFundraisesActivity />} />

            <Route
              path={routeDirectory.fundraise.rawPath}
              element={<FundraiseActivity />} />

            <Route
              path={routeDirectory.legal.rawPath}
              element={<LegalActivity />} />

            <Route
              path={routeDirectory.legal.documents.rawPath}
              element={<LegalDocumentActivity />} />

            <Route
              path={routeDirectory.beforeAndAfter.rawPath}
              element={<BeforeAndAfterActivity />} />

            <Route
              path={routeDirectory.debug.rawPath}
              element={<DebugActivity />} />
          </Routes>

          <RestrictToSubscriptionProvider.FC />
        </BrowserRouter>

        <MarkerWidget.FC />

        <ModalProvider.FC />
        <SnackbarProvider.FC />
      </ThemeProvider>
    </ApolloProvider>
  ) : null;
}

export default App;
